import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Receipt, Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
      console.error('Error fetching invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error('Error downloading PDF:', error);
    }
  };

  const updatePaymentStatus = async (invoiceId, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        `${API}/invoices/${invoiceId}/status`,
        { paid_status: status },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Payment status updated!');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error updating status:', error);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    const token = localStorage.getItem('token');
    if (!confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Invoice deleted successfully!');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
      console.error('Error deleting invoice:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="invoices-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Invoices</h1>
              <p className="text-gray-400">Manage client invoices and payments</p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="py-12 text-center">
              <Receipt className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No invoices yet. Convert quotes to invoices to get started!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {invoices.map((invoice, index) => (
              <Card key={invoice.id} data-testid={`invoice-card-${index}`} className="bg-white/5 backdrop-blur-sm border-white/10 hover:border-orange-500/30">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl text-white mb-2">
                        {invoice.invoice_number} - {invoice.client_name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span>{invoice.client_email}</span>
                        <span>{invoice.client_phone}</span>
                        <span>{formatDate(invoice.created_at)}</span>
                      </div>
                      {invoice.quote_id && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Converted from Quote</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-2xl font-bold text-white">${invoice.total.toFixed(2)}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Payment Status:</span>
                      <Select
                        value={invoice.paid_status}
                        onValueChange={(value) => updatePaymentStatus(invoice.id, value)}
                      >
                        <SelectTrigger data-testid={`payment-status-${index}`} className="w-32 bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-white/20">
                          <SelectItem value="unpaid" className="text-white hover:bg-white/10">Unpaid</SelectItem>
                          <SelectItem value="partial" className="text-white hover:bg-white/10">Partial</SelectItem>
                          <SelectItem value="paid" className="text-white hover:bg-white/10">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        invoice.paid_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        invoice.paid_status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {invoice.paid_status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex gap-2 ml-auto">
                      <Button
                        data-testid={`download-invoice-pdf-${index}`}
                        onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      
                      <Button
                        data-testid={`delete-invoice-${index}`}
                        onClick={() => deleteInvoice(invoice.id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default InvoicesPage;
